import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Video } from '../Video/Video';
import {
  AudioTrack,
  NKConfig,
  NKPlayerProps,
  NKPlayerRef,
  NKSource,
  OnProgressDataWithItemKey,
  PlayList,
  TextTrack,
} from '../types/nkPlayerTypes';

const NKPlayer = forwardRef<NKPlayerRef, NKPlayerProps>((props, ref) => {
  const videoRef = useRef(null);

  let {
    source: playerSource,
    config: playerConfig,
    playList: playerPlayList,
    style,
    insets,
    theme,
    onBackPress,
    onFullScreenModeUpdate,
    onToggleFullScreen,
    onPlay,
    onPause,
    onStop,
    onSkipNext,
    onSkipBack,
    onVolumeUpdate,
    onBrightnessUpdate,
    onMuteUpdate,
    onRepeatUpdate,
    onSpeedUpdate,
    onPlayListItemSelect,
    onScreenLockUpdate,
    onLoad,
    onEnd,
    onLoadStart,
    onError,
    onPlayProgress,
    onPlaybackStateProgress,
    captions,
    screenName,
    onTranscriptChange,
  } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [playList, setPlayList] = useState<PlayList | undefined>(playerPlayList);

  const [NKConfig, setNKConfig] = useState<NKConfig | undefined>({
    loaderText: 'Loading...',
    errorText: 'Error...!',
    doubleTapTime: 300,
    controlTimeout: 5000,
    controlHideMode: 'touch',
    layoutMode: 'intermediate',
    posterResizeMode: 'cover',
    resizeMode: 'contain',

    volume: 80,
    brightness: 25,
    playbackSpeed: '1.0',
    muted: false,
    repeat: false,
    autoPlay: false,

    disableOnScreenPlayButton: false,
    disableBack: false,
    disableResizeMode: false,
    disableMore: false,
    disableSkip: false,
    disableStop: false,
    disableVolume: false,
    disableFullscreen: false,
    disablePlayList: false,
    disableSubtitle: false,
    ...playerConfig,
  });

  const [paused, setPaused] = useState(!NKConfig?.autoPlay);
  const [playerVolume, setPlayerVolume] = useState<number>(NaN);

  const [trackInfo, setTrackInfo] = useState({
    trackTime: 0,
    totalTrackTime: 0,
    cachedTrackTime: 0,
  });

  const durationTime = useRef(0);
  const cachedTime = useRef(0);
  const currentTime = useRef(0);

  const isSeeking = useRef(false);
  const isSliding = useRef(false);
  const isSeekable = useRef(false);
  const gestureEnabled = useRef(false);
  const isStopped = useRef(false);
  const isEnded = useRef(false);
  const isVolumeSeekable = useRef(true);
  const posterDisabled = useRef(false);
  const showPoster = useRef(true);

  const [dropdownTextTracks, setDropdownTextTracks] = useState<
    ReadonlyArray<TextTrack> | undefined
  >();

  const [dropdownAudioTracks, setDropdownAudioTracks] = useState<
    ReadonlyArray<AudioTrack> | undefined
  >();

  const [NKSource, setNKSource] = useState<any>(playerSource);

  const _onLoad = useCallback((data: any) => {
    setLoading(false);
    setError(false);
    onLoad?.(NKConfig?.optimize ? NKConfig.index : playList?.currentIndex);
    onLoadStart?.(data);
  }, []);

  const _onProgress = useCallback(
    (data: any) => {
      const itemKey =
        playerPlayList?.items?.[playList?.currentIndex ?? 0]?.itemSource?.itemKey ?? '';
      const videoProgressData: OnProgressDataWithItemKey = { ...data, itemKey: itemKey };

      onPlayProgress?.(videoProgressData);
    },
    [onPlayProgress]
  );

  const _onEnd = useCallback(() => {
    isEnded.current = true;
    onEnd?.();
  }, [paused, NKConfig, onEnd]);

  const _onError = useCallback((error: any) => {
    setError(true);
    onError?.(error?.error?.errorString?.toString() ?? 'Unhandled Error');
  }, []);

  const _onPlaybackStateChanged = useCallback(
    (e: Readonly<{ isPlaying: boolean }>) => {
      onPlaybackStateProgress?.(e);
    },
    [onPlaybackStateProgress]
  );

  const newSource: NKSource = playList?.items
    ? playList?.items.length !== 0 && (playList?.currentIndex ?? -1) > -1
      ? playList?.items?.[playList?.currentIndex ?? -1]?.itemSource
      : NKSource
    : NKSource;

  if (newSource?.itemKey) newSource['videoId'] = newSource?.itemKey;

  useEffect(() => {
    const newSource = {
      ...NKSource,
      ...playerSource,
    };
    if (newSource.poster) {
      showPoster.current = true;
    } else {
      showPoster.current = false;
    }
    setNKSource(newSource);
  }, [playerSource]);

  useEffect(() => {
    setPlayList((prevState) => {
      return {
        ...prevState,
        ...playerPlayList,
      };
    });
  }, [playerPlayList]);

  // console.log("Video source => ", newSource);

  return (
    <>
      {newSource?.source?.uri != '' && (
        <>
          <Video
            source={newSource?.source || ''}
            poster={newSource?.poster}
            onEnd={_onEnd}
            onPause={() => onPause?.(NKConfig?.optimize ? NKConfig.index : playList?.currentIndex)}
            onError={(error) => _onError(error)}
            onProgress={(data) => _onProgress(data)}
            onLoad={(data) => _onLoad(data)}
            onPlaybackStateChanged={(e) => _onPlaybackStateChanged(e)}
            title={newSource?.title}
            NKTheme={theme}
            controls={true}
            muted={NKConfig?.muted}
            captions={captions}
            adTagUrl={
              'http://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/ad_rule_samples&ciu_szs=300x250&ad_rule=1&impl=s&gdfp_req=1&env=vp&output=xml_vmap1&unviewed_position_start=1&cust_params=sample_ar%3Dpremidpostpod%26deployment%3Dgmf-js&cmsid=496&vid=short_onecue&correlator='
            }
            screenName={screenName}
            style={style}
          onTranscriptChange={onTranscriptChange}
          />
        </>
      )}
    </>
  );
});
export default NKPlayer;
